import { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { navRoutes } from "routes/routes";
import { AppContext } from "context/AppContext";
import { MobileMenu } from "components/MobileMenu/MobileMenu";

export const Navbar = ({ countries }) => {
	const intl = useIntl();
	const { countryData, setCurrentCountry } = useContext(AppContext);
	const [countriesList, setCountriesList] = useState([]);
	const [scrolled, setScrolled] = useState(false);
	const [countryOpen, setCountryOpen] = useState(false);
	const [activeSection, setActiveSection] = useState("#home");

	const { flag, text } = countryData;

	useEffect(() => {
		if (countriesList.length === 0) {
			setCountriesList(countries);
		}
	}, [countries, countriesList.length]);

	// Detectar scroll para cambiar estilo del navbar
	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 20);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Scroll spy - detectar sección activa
	useEffect(() => {
		const sectionIds = ['home', 'calculator', 'faqs', 'downloads', 'how-works', 'contact'];

		const observerOptions = {
			root: null,
			rootMargin: '-20% 0px -70% 0px', // Activar cuando la sección esté en el 20-30% superior del viewport
			threshold: 0,
		};

		const observerCallback = (entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setActiveSection(`#${entry.target.id}`);
				}
			});
		};

		const observer = new IntersectionObserver(observerCallback, observerOptions);

		sectionIds.forEach((id) => {
			const element = document.getElementById(id);
			if (element) {
				observer.observe(element);
			}
		});

		return () => {
			sectionIds.forEach((id) => {
				const element = document.getElementById(id);
				if (element) {
					observer.unobserve(element);
				}
			});
		};
	}, []);

	const handleOnSelectChange = (value) => {
		const newCountry = countries.find(
			(country) => country.country_code === value
		);
		if (newCountry) {
			setCurrentCountry(newCountry);
		}
	};

	const handleOnClick = (elementId) => {
		const element = document.querySelector(`#${elementId}`);

		if (element.classList.contains("h-auto")) {
			element.classList.remove("h-auto");
			element.classList.add("h-0");
		} else {
			element.classList.remove("h-0");
			element.classList.add("h-auto");
		}
	};

	return (
		<header
			className={`
				fixed z-40 top-0 right-0 left-0
				flex justify-between items-center
				h-16 md:h-20 px-4 md:px-8 lg:px-12
				transition-all duration-300
				${scrolled
					? 'bg-white/95 backdrop-blur-md shadow-subtle border-b border-surface-100'
					: 'bg-white'
				}
			`}
		>
			{/* Logo */}
			<div className="flex-shrink-0">
				<a href="/" className="flex items-center">
					<img
						className="h-10 md:h-12 w-auto object-contain"
						src={`${process.env.PUBLIC_URL}/assets/efektivos_logo.png`}
						alt="Efektivos"
					/>
				</a>
			</div>

			{/* Mobile Menu */}
			<MobileMenu
				countriesList={countriesList}
				flag={flag}
				text={text}
				handleOnClick={handleOnClick}
				handleOnSelectChange={handleOnSelectChange}
				activeSection={activeSection}
			/>

			{/* Desktop Navigation */}
			<nav className="hidden xl:flex items-center gap-1">
				{navRoutes(intl).map((route) => (
					<a
						key={route.path}
						href={route.path}
						className={`
							relative px-4 py-2 text-body-sm font-medium
							transition-colors duration-200
							${activeSection === route.path
								? 'text-accent'
								: 'text-surface-600 hover:text-primary'
							}
						`}
					>
						{route.label}
						{activeSection === route.path && (
							<span className="absolute bottom-0 left-4 right-4 h-0.5 bg-accent rounded-full" />
						)}
					</a>
				))}
			</nav>

			{/* Actions */}
			<div className="hidden xl:flex items-center gap-4">
				{/* Social Links */}
				<div className="flex items-center gap-2">
					<a
						href="https://www.facebook.com/gt.efektivos"
						target="_blank"
						rel="noreferrer"
						className="
							flex items-center justify-center
							w-9 h-9 rounded-lg
							bg-surface-100 hover:bg-accent hover:text-white
							text-surface-600
							transition-all duration-200
						"
						aria-label="Facebook"
					>
						<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
						</svg>
					</a>
					<a
						href="https://www.instagram.com/efektivos/?hl=es"
						target="_blank"
						rel="noreferrer"
						className="
							flex items-center justify-center
							w-9 h-9 rounded-lg
							bg-surface-100 hover:bg-accent hover:text-white
							text-surface-600
							transition-all duration-200
						"
						aria-label="Instagram"
					>
						<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
						</svg>
					</a>
				</div>

				{/* Country Selector */}
				<div className="relative">
					<button
						onClick={() => setCountryOpen(!countryOpen)}
						className="
							flex items-center gap-2 px-3 py-2
							bg-surface-50 hover:bg-surface-100
							border border-surface-200
							rounded-lg transition-colors duration-200
						"
					>
						<img
							className="w-6 h-6 rounded-full object-cover"
							src={flag}
							alt={text}
						/>
						<svg
							className={`w-4 h-4 text-surface-400 transition-transform duration-200 ${countryOpen ? 'rotate-180' : ''}`}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
						</svg>
					</button>

					{countryOpen && (
						<div className="absolute top-full right-0 mt-2 min-w-[180px] bg-white border border-surface-200 rounded-lg shadow-lg z-50">
							{countriesList.map((country) => (
								<button
									key={country.uid}
									onClick={() => {
										handleOnSelectChange(country.country_code);
										setCountryOpen(false);
									}}
									className="
										flex items-center gap-3 w-full px-4 py-3
										hover:bg-surface-50 transition-colors
										first:rounded-t-lg last:rounded-b-lg
									"
								>
									<img
										src={country.flag_url}
										alt={country.name}
										className="w-6 h-6 rounded-full object-cover"
									/>
									<span className="text-body-sm text-surface-700">{country.name}</span>
								</button>
							))}
						</div>
					)}
				</div>
			</div>
		</header>
	);
};
